import {Palette} from 'hubbl-shared';
import {memo, useCallback, useEffect, useMemo, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import styled from 'styled-components';
import {Api} from '../../api';
import {useAppSelector, useI18n} from '../../hooks';
import {AdminWebRoute} from '../../routes/AdminWebRoute';
import {RootRoute} from '../../routes/RootRoute';
import {CompanySelector} from '../../store/company/CompanySelector';
import {UserSelector} from '../../store/user/UserSelector';
import {
  TextHeading3,
  TextHeading7,
  TextHeading8,
  WhiteContainer,
} from '../index';
import {InviteUsersModal} from '../InviteUsers/InviteUsersModal';
import {AnimatedProgressBar} from './AnimatedProgressBar';
import {AssetIconChevron} from '../../assets/icons/icon-chevron/AssetIconChevron';

interface OnboardingStep {
  id: string;
  label: string;
  completed: boolean;
  route: string;
}

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
`;

const TodoListContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const TodoItem = styled.div<{completed: boolean; clickable: boolean}>`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  border-radius: 8px;
  cursor: ${props => (props.clickable ? 'pointer' : 'default')};
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  background: ${props =>
    props.completed ? Palette.primarySubtleGreen : 'transparent'};
  border: 1px solid
    ${props =>
      props.completed
        ? Palette.primaryPadua + '30'
        : Palette.almostBlackTransparent10};

  &:hover {
    ${props =>
      props.clickable &&
      `
      background: ${props.completed ? Palette.primarySubtleGreen : Palette.almostBlackTransparent5};
      transform: translateX(2px);
      border-color: ${Palette.primaryPadua}60;
    `}
  }
`;

const Checkbox = styled.div<{completed: boolean}>`
  width: 20px;
  height: 20px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${props =>
    props.completed ? Palette.primaryPadua : Palette.white};
  border: 2px solid
    ${props =>
      props.completed
        ? Palette.primaryPadua
        : Palette.almostBlackTransparent10};
  flex-shrink: 0;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);

  ${TodoItem}:hover & {
    border-color: ${props =>
      props.completed ? Palette.primaryPadua : Palette.primaryPadua + '80'};
  }
`;

const Checkmark = styled.div`
  color: ${Palette.white};
  font-size: 10px;
  font-weight: bold;
  line-height: 1;
`;

const TodoLabel = styled(TextHeading8)<{completed: boolean}>`
  flex: 1;
  color: ${props =>
    props.completed ? Palette.almostBlack : Palette.almostBlack500};
  text-decoration: ${props => (props.completed ? 'line-through' : 'none')};
  opacity: ${props => (props.completed ? 0.7 : 1)};
  transition: all 0.2s ease;
  font-size: ${props => (props.completed ? '12px' : 'inherit')};
  line-height: ${props => (props.completed ? '1.3' : 'inherit')};
`;

const HeaderContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
  cursor: pointer;
  user-select: none;
`;

const ProgressText = styled(TextHeading7)`
  color: ${Palette.almostBlack500};
  font-size: 14px;
  font-weight: 500;
  flex: 1;
`;

const ChevronButton = styled.button<{isExpanded: boolean}>`
  background: none;
  border: none;
  padding: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  transform: rotate(${props => (props.isExpanded ? '90deg' : '0deg')});
  color: ${Palette.almostBlack500};
  flex-shrink: 0;

  &:hover {
    opacity: 0.7;
  }
`;

const CollapsibleContent = styled.div<{isExpanded: boolean}>`
  max-height: ${props => (props.isExpanded ? '1000px' : '0')};
  overflow: hidden;
  transition:
    max-height 0.3s cubic-bezier(0.4, 0, 0.2, 1),
    opacity 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  opacity: ${props => (props.isExpanded ? 1 : 0)};
`;

const ProgressBarContainer = styled.div<{isCollapsed?: boolean}>`
  margin-bottom: ${props => (props.isCollapsed ? '0' : '16px')};
  ${props =>
    props.isCollapsed &&
    `
    display: flex;
    align-items: center;
    flex: 1;
  `}
`;

const LoadingContainer = styled.div`
  padding: 16px;
  text-align: center;
  color: ${Palette.almostBlack500};
`;

const STORAGE_KEY = 'adminOnboardingProgressCollapsed';

export const AdminOnboardingProgress = memo(() => {
  const userDetails = useAppSelector(UserSelector.getUserDetails);
  const companyDetails = useAppSelector(CompanySelector.getCompanyDetails);
  const navigate = useNavigate();
  const t = useI18n();
  const [loading, setLoading] = useState(true);
  const [steps, setSteps] = useState<OnboardingStep[]>([]);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [isExpanded, setIsExpanded] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved !== 'true';
  });

  const checkOnboardingStatus = useCallback(async () => {
    if (!userDetails?.companyId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);

      // Check all 4 steps
      const [invitedUsersCount, eventsResult, activeUsersCount] =
        await Promise.all([
          Api.invitedUsers.getPendingInvitedUsersCount(userDetails.companyId),
          Api.event.getEvents(userDetails.companyId, 1, null),
          Api.user.getUsersCount(userDetails.companyId),
        ]);

      // Step 1: Invite Colleagues - check if there are invited users OR active users > 1 (admin + at least one other)
      const hasInvitedColleagues =
        invitedUsersCount > 0 || activeUsersCount > 1;

      // Step 2: Integrate Slack/Teams - check company settings (general + sub-company level)
      const hasSlackIntegration =
        !!companyDetails?.settings?.slackIntegration?.webhookUrl ||
        Object.values(companyDetails?.organizationalMetadata || {}).some(
          location =>
            Object.values(location.subCompanies || {}).some(
              subCompany => !!subCompany.slackWebhookUrl,
            ),
        );
      const hasTeamsIntegration =
        !!companyDetails?.settings?.teamsIntegration?.webhookUrl ||
        Object.values(companyDetails?.organizationalMetadata || {}).some(
          location =>
            Object.values(location.subCompanies || {}).some(
              subCompany => !!subCompany.teamsWebhookUrl,
            ),
        );
      const hasMessagingIntegration =
        hasSlackIntegration || hasTeamsIntegration;

      // Step 3: Connect Calendar - check user settings
      const hasCalendarIntegration =
        userDetails?.settings?.calendarIntegration !== undefined &&
        userDetails.settings.calendarIntegration !== null;

      // Step 4: Create First Event - check if events exist
      const hasCreatedEvent = eventsResult.events.length > 0;

      setSteps([
        {
          id: 'invite-colleagues',
          label: t.dashboard.inviteColleagues,
          completed: hasInvitedColleagues,
          route: `${RootRoute.AuthAdminHome}${AdminWebRoute.EmployeesOverview}`,
        },
        {
          id: 'integrate-messaging',
          label: t.dashboard.integrateSlackTeams,
          completed: hasMessagingIntegration,
          route: `${RootRoute.AuthAdminHome}${AdminWebRoute.Settings}/${AdminWebRoute.SettingsIntegrations}`,
        },
        {
          id: 'connect-calendar',
          label: t.dashboard.connectCalendar,
          completed: hasCalendarIntegration,
          route: `${RootRoute.AuthAdminHome}${AdminWebRoute.Settings}/${AdminWebRoute.SettingsGeneral}`,
        },
        {
          id: 'create-event',
          label: t.dashboard.createFirstEvent,
          completed: hasCreatedEvent,
          route: `${RootRoute.AuthAdminHome}${AdminWebRoute.CreateEvent}`,
        },
      ]);
    } catch (error) {
      console.error('Error checking onboarding status:', error);
    } finally {
      setLoading(false);
    }
  }, [userDetails, companyDetails, t]);

  useEffect(() => {
    checkOnboardingStatus();
  }, [checkOnboardingStatus]);

  const completedCount = useMemo(
    () => steps.filter(step => step.completed).length,
    [steps],
  );

  const allCompleted = useMemo(
    () => completedCount === 4 && steps.length === 4,
    [completedCount, steps.length],
  );

  const hasIncompleteSteps = useMemo(
    () => completedCount < 4 && steps.length > 0,
    [completedCount, steps.length],
  );

  const completionPercentage = useMemo(() => {
    return (completedCount / 4) * 100;
  }, [completedCount]);

  const handleStepClick = useCallback(
    (step: OnboardingStep) => {
      if (step.completed) return;

      if (step.id === 'invite-colleagues') {
        setShowInviteModal(true);
      } else {
        navigate(step.route);
      }
    },
    [navigate],
  );

  const toggleExpanded = useCallback(() => {
    setIsExpanded(prev => {
      const newValue = !prev;
      localStorage.setItem(STORAGE_KEY, (!newValue).toString());
      return newValue;
    });
  }, []);

  if (loading) {
    return (
      <WhiteContainer>
        <LoadingContainer>
          <TextHeading7 text={t.common.loading} />
        </LoadingContainer>
      </WhiteContainer>
    );
  }

  if (allCompleted) {
    return null;
  }

  return (
    <>
      <SectionHeader>
        <TextHeading3 text={t.common.gettingStarted} />
      </SectionHeader>
      <WhiteContainer contentPadding={16}>
        <HeaderContainer onClick={toggleExpanded}>
          <ProgressText
            text={
              t.dashboard.stepsCompleted
                .replace('{completed}', completedCount.toString())
                .replace('{total}', '4') +
              (hasIncompleteSteps ? ` (${t.dashboard.clickToComplete})` : '')
            }
          />
          <ChevronButton isExpanded={isExpanded} type="button">
            <AssetIconChevron size={16} color={Palette.almostBlack500} />
          </ChevronButton>
        </HeaderContainer>
        <ProgressBarContainer>
          <AnimatedProgressBar
            percentage={completionPercentage}
            duration={1500}
          />
        </ProgressBarContainer>
        <CollapsibleContent isExpanded={isExpanded}>
          <TodoListContainer>
            {steps.map((step, index) => (
              <TodoItem
                key={step.id}
                completed={step.completed}
                clickable={!step.completed}
                onClick={() => handleStepClick(step)}>
                <Checkbox completed={step.completed}>
                  {step.completed && <Checkmark>✓</Checkmark>}
                </Checkbox>
                <TodoLabel completed={step.completed} text={step.label} />
              </TodoItem>
            ))}
          </TodoListContainer>
        </CollapsibleContent>
      </WhiteContainer>

      {userDetails?.companyId && (
        <InviteUsersModal
          isOpen={showInviteModal}
          onClose={() => setShowInviteModal(false)}
          companyId={userDetails.companyId}
        />
      )}
    </>
  );
});
